import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import useHandleErrors from '../../hooks/useHandleErrors';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../assets/logo.png';
import {
    Box,
    InputAdornment,
    IconButton,
    Link,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LandingNavbar } from '../Welcome';
import { noAutoFormat } from '../../global/helpers';
import { useAutofillSync } from '../../hooks/useAutofillSync';

const Login = () => {
    const [search] = useSearchParams();
    const { Login: AuthLogin, logged } = useAuth();
    const navigation = useNavigate();
    const [hasError, setError] = useState();
    const [showPassword, setShowPassword] = useState(false);
    useHandleErrors(hasError);

    const { values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting } =
        useFormik({
            initialValues: { email: '', password: '' },
            onSubmit: async (values, { setSubmitting }) => {
                try {
                    setSubmitting(true);
                    const { data } = await server.post('auth', values);
                    AuthLogin(data.data);

                    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');

                    if (redirectUrl) {
                        sessionStorage.removeItem('redirectAfterLogin');
                        navigation(redirectUrl);
                    } else if (search.get('sign') === 'encontrado') {
                        navigation('/perfil');
                    } else {
                        navigation('/lista');
                    }
                    setSubmitting(false);
                } catch (error) {
                    setSubmitting(false);
                    setError(error);
                }
            },
        });

    // Sem isso, o que o navegador preenche sozinho nao chega ao formik: o botao
    // Entrar fica desabilitado e um envio mandaria e-mail e senha vazios
    const formRef = useRef(null);
    useAutofillSync(formRef, ['email', 'password'], (nome, valor) => {
        const normalizado = nome === 'email' ? valor.toLowerCase() : valor;
        if (values[nome] !== normalizado) setFieldValue(nome, normalizado);
    });

    useEffect(() => {
        if (logged) {
            const redirectUrl = sessionStorage.getItem('redirectAfterLogin');

            if (redirectUrl) {
                sessionStorage.removeItem('redirectAfterLogin');
                navigation(redirectUrl);
            } else {
                navigation('/lista');
            }
        }
    }, [logged, navigation]);

    return (
        <>
            <LandingNavbar />
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                p: { xs: '16px 12px', sm: '24px 16px' },
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: 420,
                    gap: 3,
                }}>
                    {/* Card do formulario */}
                    <Box sx={{
                        width: '100%',
                        bgcolor: 'white',
                        borderRadius: '16px',
                        border: '1px solid #e8e8e8',
                        p: { xs: '24px 20px', sm: '32px 28px' },
                    }}>
                        <Stack spacing={0.5} alignItems="center" sx={{ mb: 3 }}>
                            <Box
                                component="img"
                                src={logo}
                                sx={{ width: { xs: 100, sm: 120 }, mb: 1 }}
                            />
                            <Typography variant="h6" fontWeight={600} color="#333">
                                Entrar na sua conta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Acesse a plataforma Empiria
                            </Typography>
                        </Stack>

                        <form onSubmit={handleSubmit} ref={formRef}>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="E-mail ou nome de usuário"
                                    name="email"
                                    value={values.email}
                                    onChange={e => setFieldValue('email', e.target.value.toLowerCase())}
                                    onBlur={handleBlur}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email}
                                    inputProps={noAutoFormat}
                                    // O navegador preenche sem disparar onChange: sem
                                    // shrink fixo, a label continua grande por cima do
                                    // texto preenchido e so sobe quando o campo recebe foco
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Senha"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(errors.password)}
                                    helperText={errors.password}
                                    inputProps={noAutoFormat}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end" size="small">
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <LoadingButton
                                    type="submit"
                                    fullWidth
                                    loading={isSubmitting}
                                    variant="contained"
                                    disabled={!(values.email && values.password) || isSubmitting}
                                    sx={{
                                        bgcolor: '#006a61',
                                        borderRadius: '8px',
                                        height: 44,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        '&:hover': { bgcolor: '#005a52' },
                                    }}
                                >
                                    Entrar
                                </LoadingButton>

                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    Não possui uma conta?{' '}
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => navigation('/criar-conta')}
                                        sx={{ cursor: 'pointer', color: '#006a61', fontWeight: 500 }}
                                    >
                                        Registre-se agora.
                                    </Link>
                                </Typography>

                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={() => navigation('/recuperar-senha')}
                                    sx={{ cursor: 'pointer', color: 'text.secondary', textAlign: 'center', fontSize: '0.8rem' }}
                                >
                                    Esqueceu a senha?
                                </Link>
                            </Stack>
                        </form>
                    </Box>

                    {/* Voltar */}
                    <Typography
                        variant="body2"
                        onClick={() => navigation('/')}
                        sx={{
                            color: '#006a61',
                            cursor: 'pointer',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Voltar para a página inicial
                    </Typography>

                    {/* Footer */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            <Link href="/chat-bot" sx={{ color: 'text.secondary' }}>Suporte</Link>
                            {' - '}
                            <Link href="/politicas" sx={{ color: 'text.secondary' }}>Termos e Políticas</Link>
                            {' - '}
                            Versão {localStorage.getItem('current_version')}
                        </Typography>
                    </Stack>
                </Box>
            </Box>
        </>
    );
};

export default Login;
